import React, { useState } from "react";
import { Flag, Loader2, ShieldX } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../ui/dialog";
import { RadioGroup, RadioGroupItem } from "../ui/radio-group";
import { Textarea } from "../ui/textarea";
import api from "../../api";
import { useAuth } from "../../context/AuthContext";
import { useToast } from "../../hooks/use-toast";
import { useNavigate } from "react-router-dom";
import { useLang } from "../../context/LanguageContext";

const REASONS = [
  { value: "spam", key: "rpt.spamOrMisleading" },
  { value: "wrong_price", key: "rpt.wrongOrFakePrice" },
  { value: "offensive", key: "rpt.offensiveOrInappropriate" },
  { value: "expired", key: "rpt.expiredNoLongerAvailable" },
  { value: "other", key: "rpt.somethingElse" },
];

export const ReportDealDialog = ({ dealId, trigger }) => {
  const { t } = useLang();
  const { user } = useAuth();
  const { toast } = useToast();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState("spam");
  const [details, setDetails] = useState("");
  const [loading, setLoading] = useState(false);

  const openDialog = () => {
    if (!user) {
      toast({ title: t("rpt.signInToReport"), description: t("rpt.pleaseLogInTo") });
      navigate("/login");
      return;
    }
    setReason("spam"); setDetails(""); setOpen(true);
  };

  const submit = async () => {
    setLoading(true);
    try {
      const label = t(REASONS.find((r) => r.value === reason)?.key || reason);
      const text = details.trim() ? `${label} — ${details.trim()}` : label;
      await api.post(`/deals/${dealId}/report`, { reason: text });
      setOpen(false);
      toast({ title: t("rpt.thanksForTheReport"), description: t("rpt.ourTeamWillReview") });
    } catch (err) {
      toast({ title: t("rpt.couldNotSubmitReport"), description: err.response?.data?.detail || "Please try again." });
    } finally { setLoading(false); }
  };

  return (
    <>
      {trigger ? React.cloneElement(trigger, { onClick: openDialog }) : (
        <button onClick={openDialog} data-testid="report-deal-btn" title={t("rpt.reportThisDeal")} className="h-11 w-11 rounded-xl border border-border flex items-center justify-center text-muted-foreground hover:text-rose-500 hover:border-rose-400 transition-colors" aria-label={t("rpt.reportDeal")}>
          <Flag size={19} />
        </button>
      )}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-md" data-testid="report-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Flag size={18} className="text-rose-500" /> {t("rpt.reportThisDeal")}</DialogTitle>
            <DialogDescription>{t("rpt.tellUsWhatS")}</DialogDescription>
          </DialogHeader>
          <RadioGroup value={reason} onValueChange={setReason} className="space-y-1.5 my-1">
            {REASONS.map((r) => (
              <label key={r.value} htmlFor={`reason-${r.value}`} data-testid={`reason-${r.value}`} className={`flex items-center gap-3 rounded-xl border px-3 py-2.5 cursor-pointer transition-colors ${reason === r.value ? "border-[#16a34a] bg-[#e7f6ec] dark:bg-[#16341f]" : "border-border hover:bg-secondary"}`}>
                <RadioGroupItem value={r.value} id={`reason-${r.value}`} />
                <span className="text-sm font-medium text-foreground">{t(r.key)}</span>
              </label>
            ))}
          </RadioGroup>
          <Textarea value={details} onChange={(e) => setDetails(e.target.value)} placeholder={t("rpt.addAnyDetailsOptional")} maxLength={400} rows={3} data-testid="report-details" />
          <DialogFooter className="gap-2 sm:gap-2">
            <button onClick={() => setOpen(false)} className="text-sm font-semibold text-muted-foreground hover:text-foreground px-4 py-2.5">Cancel</button>
            <button onClick={submit} disabled={loading} data-testid="submit-report-btn" className="inline-flex items-center justify-center gap-2 bg-rose-500 hover:bg-rose-600 disabled:opacity-60 text-white text-sm font-semibold px-5 py-2.5 rounded-xl transition-colors">{loading && <Loader2 className="animate-spin" size={15} />} Submit report</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export const NotHonouredButton = ({ dealId, className = "" }) => {
  const { t } = useLang();
  const { user } = useAuth();
  const { toast } = useToast();
  const navigate = useNavigate();
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  const send = async () => {
    if (!user) {
      toast({ title: t("rpt.signInToReport"), description: t("rpt.pleaseLogInTo") });
      navigate("/login");
      return;
    }
    setBusy(true);
    try {
      await api.post(`/deals/${dealId}/report`, { reason: t("rpt.notHonoured"), code: "not_honoured" });
      setDone(true);
      toast({ title: t("rpt.notHonouredThanks"), description: t("rpt.notHonouredNote") });
    } catch (err) {
      toast({ title: t("rpt.couldNotSubmitReport"), description: err.response?.data?.detail || "Please try again." });
    } finally { setBusy(false); }
  };

  return (
    <button onClick={send} disabled={busy || done} data-testid="not-honoured-btn"
      className={`inline-flex items-center justify-center gap-2 rounded-xl border border-rose-300/70 text-rose-600 hover:bg-rose-50 dark:hover:bg-rose-950/30 disabled:opacity-60 text-sm font-semibold px-4 py-2.5 transition-colors ${className}`}>
      {busy ? <Loader2 className="animate-spin" size={15} /> : <ShieldX size={15} />}
      {done ? t("rpt.notHonouredSent") : t("rpt.notHonoured")}
    </button>
  );
};

export default ReportDealDialog;
